import React, { useState, useEffect, useCallback } from 'react';
import { checkBathroomConstraints } from '../utils/bathroomConstraintChecker';
import { ruleToLabel } from '../utils/constraintFilter';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Module scope, not inside the modal: a component created during render is
// a new type on every render, so React remounts it instead of updating it —
// which drops any click whose mousedown and mouseup straddle a re-render.
// Every toggle here re-renders the modal, so all of them were being rebuilt
// each time one was flipped.
const ToggleSwitch = ({ enabled, onToggle }) => (
  <div
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    style={{
      width: '36px',
      height: '20px',
      borderRadius: '10px',
      backgroundColor: enabled ? '#22c55e' : '#d1d5db',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      position: 'relative',
      flexShrink: 0,
    }}
  >
    <div
      style={{
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        position: 'absolute',
        top: '2px',
        left: enabled ? '18px' : '2px',
        transition: 'left 0.2s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }}
    />
  </div>
);

/**
 * Bathroom constraints modal.
 * Shows individual constraint violations with toggles to disable specific ones.
 * Disabled rules are saved per-room in Firestore.
 */
const BathroomConstraintsModal = ({
  isOpen,
  onClose,
  roomData,
  structureData,
  disabledConstraints = [],
  onDisabledConstraintsChange,
}) => {
  const [results, setResults] = useState(null);
  const [localDisabled, setLocalDisabled] = useState(disabledConstraints);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalDisabled(disabledConstraints);
  }, [disabledConstraints]);

  useEffect(() => {
    if (isOpen && roomData) {
      try {
        const r = checkBathroomConstraints(
          roomData,
          localDisabled,
          structureData
        );
        setResults(r);
      } catch (err) {
        console.error('Constraint check failed:', err);
        setResults(null);
      }
    } else {
      setResults(null);
    }
  }, [isOpen, roomData, structureData, localDisabled]);

  const toggleRule = useCallback(
    async (rule) => {
      const isCurrentlyDisabled = localDisabled.includes(rule);
      const updated = isCurrentlyDisabled
        ? localDisabled.filter((r) => r !== rule)
        : [...localDisabled, rule];

      setLocalDisabled(updated);
      if (onDisabledConstraintsChange) {
        onDisabledConstraintsChange(updated);
      }

      if (roomData?.id) {
        setSaving(true);
        try {
          const roomRef = doc(db, 'rooms', roomData.id);
          await updateDoc(roomRef, {
            disabledConstraints: updated,
            lastModified: serverTimestamp(),
          });
        } catch (err) {
          console.error('Failed to save disabled constraints:', err);
        }
        setSaving(false);
      }
    },
    [localDisabled, roomData?.id, onDisabledConstraintsChange]
  );

  const clearAllDisabled = useCallback(async () => {
    setLocalDisabled([]);
    if (onDisabledConstraintsChange) {
      onDisabledConstraintsChange([]);
    }
    if (roomData?.id) {
      setSaving(true);
      try {
        const roomRef = doc(db, 'rooms', roomData.id);
        await updateDoc(roomRef, {
          disabledConstraints: [],
          lastModified: serverTimestamp(),
        });
      } catch (err) {
        console.error('Failed to clear disabled constraints:', err);
      }
      setSaving(false);
    }
  }, [roomData?.id, onDisabledConstraintsChange]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) onClose();
  };

  const violations = results?.violations || [];
  const disabledItems = results?.passed?.filter((p) => p.disabled) || [];
  // Also show disabled rules that have no current violation (the room may have changed)
  const orphanedDisabled = localDisabled
    .filter((rule) => !disabledItems.some((d) => d.rule === rule))
    .map((rule) => ({ rule, label: ruleToLabel(rule), disabled: true }));
  const allDisabled = [...disabledItems, ...orphanedDisabled];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.2s ease-out',
        pointerEvents: 'auto',
        isolation: 'isolate',
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={{
          background: '#F3E9D2',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '460px',
          maxHeight: '80vh',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          animation: 'slideUp 0.3s ease-out',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem 1.5rem 1rem 1.5rem',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#54524F',
              }}
            >
              Bathroom Constraints
            </h3>
            {saving && (
              <span
                style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}
              >
                Saving...
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              cursor: 'pointer',
              color: '#999',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              marginRight: '-8px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              e.currentTarget.style.color = '#666';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#999';
            }}
          >
            &times;
          </button>
        </div>

        {/* No room selected */}
        {!roomData && (
          <div
            style={{
              padding: '0 1.5rem 1.5rem',
              fontSize: '0.85rem',
              color: '#94a3b8',
              fontStyle: 'italic',
            }}
          >
            Select a room to see constraint status
          </div>
        )}

        {/* Scrollable content */}
        {roomData && (
          <div
            style={{
              overflowY: 'auto',
              flex: 1,
              padding: '0 0 1rem 0',
            }}
          >
            {/* Active Violations */}
            <div
              style={{
                padding: '8px 1.5rem',
                fontSize: '0.8rem',
                fontWeight: 700,
                color: '#54524F',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                borderBottom: '1px solid rgba(186, 165, 148, 0.3)',
              }}
            >
              Violations ({violations.length})
            </div>

            {violations.length === 0 && (
              <div
                style={{
                  padding: '12px 1.5rem',
                  fontSize: '0.875rem',
                  color: '#16a34a',
                  fontWeight: 500,
                  borderBottom: '1px solid rgba(186, 165, 148, 0.3)',
                }}
              >
                All constraints satisfied
              </div>
            )}

            {violations.map((v) => (
              <div
                key={v.rule}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 1.5rem',
                  borderBottom: '1px solid rgba(186, 165, 148, 0.2)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0 }}
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  <span
                    style={{
                      fontSize: '0.875rem',
                      color: '#54524F',
                    }}
                  >
                    {v.label}
                  </span>
                </div>
                <ToggleSwitch
                  enabled={true}
                  onToggle={() => toggleRule(v.rule)}
                />
              </div>
            ))}

            {/* Disabled Rules */}
            <div
              style={{
                padding: '8px 1.5rem',
                fontSize: '0.8rem',
                fontWeight: 700,
                color: '#54524F',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                borderBottom: '1px solid rgba(186, 165, 148, 0.3)',
                marginTop: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Disabled ({allDisabled.length})</span>
              {allDisabled.length > 0 && (
                <button
                  onClick={clearAllDisabled}
                  style={{
                    fontSize: '0.7rem',
                    color: '#BAA594',
                    background: 'none',
                    border: '1px solid #BAA594',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    cursor: 'pointer',
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontWeight: 500,
                  }}
                >
                  Re-enable all
                </button>
              )}
            </div>

            {allDisabled.length === 0 && (
              <div
                style={{
                  padding: '12px 1.5rem',
                  fontSize: '0.875rem',
                  color: '#94a3b8',
                  fontStyle: 'italic',
                  borderBottom: '1px solid rgba(186, 165, 148, 0.2)',
                }}
              >
                No constraints disabled
              </div>
            )}

            {allDisabled.map((d) => (
              <div
                key={d.rule}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 1.5rem',
                  borderBottom: '1px solid rgba(186, 165, 148, 0.2)',
                  opacity: 0.6,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#94a3b8',
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  >
                    OFF
                  </span>
                  <span
                    style={{
                      fontSize: '0.875rem',
                      color: '#54524F',
                      textDecoration: 'line-through',
                    }}
                  >
                    {d.label}
                  </span>
                </div>
                <ToggleSwitch
                  enabled={false}
                  onToggle={() => toggleRule(d.rule)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BathroomConstraintsModal;
